import { faSave } from "@fortawesome/free-solid-svg-icons";
import { type FC, useState } from "react";
import { useApi } from "../api/APIContext";
import { useApiCall, ValidationError } from "../api/api";
import { type Checklist, type LiftingCategory, liftingCategoryLabels } from "../api/dto/Checklist";
import { ModalFooter } from "../components/layout/ModalProvider";
import { usePermissions } from "../permissions/usePermissions";
import { Input, Select } from "@ventumsoftware/react-toolkit";

const GLOBAL_OPTION = "-";
const ALL_LIFTING_CATEGORIES_OPTION = "";
const liftingCategoryOptions = {
  [ALL_LIFTING_CATEGORIES_OPTION]: "Alle hijscategorieën",
  ...liftingCategoryLabels,
};

export const AddChecklistModal: FC<{ onCreated: (checklist: Checklist) => void }> = (props) => {
  const { companies } = useApi();
  const { addChecklist } = useApiCall();
  const { isAdmin, companyPermissions } = usePermissions();
  const [materialType, setMaterialType] = useState<string>("");
  const [liftingCategory, setLiftingCategory] = useState<string>(ALL_LIFTING_CATEGORIES_OPTION);
  // No default selection — GLOBAL_OPTION isn't a valid choice for non-admins, so pre-selecting it
  // would leave the Select in a state that doesn't match its own options for that case.
  const [companyId, setCompanyId] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);

  // Mirrors what ChecklistPolicy::create already enforces server-side (global checklists are
  // admin-only; company-scoped ones need manage_checklists for that specific company) — this is
  // purely about not offering an option that would always 403, the real check still happens
  // backend-side regardless.
  const manageableCompanyIds = new Set(
    companyPermissions.filter((company) => company.has("manage_checklists")).map((company) => company.companyId),
  );
  const selectableCompanies = companies
    .filter((company) => !company.isArchived)
    .filter((company) => isAdmin || manageableCompanyIds.has(company.id));
  const companyOptions = [
    ["", "Kies een bedrijf"],
    ...(isAdmin ? [[GLOBAL_OPTION, "Globaal (geen specifiek bedrijf)"]] : []),
    ...selectableCompanies.map((company) => [company.id, company.name]),
  ];

  const save = async () => {
    const checklist = await addChecklist(
      materialType,
      liftingCategory === ALL_LIFTING_CATEGORIES_OPTION ? null : (liftingCategory as LiftingCategory),
      companyId === GLOBAL_OPTION ? null : companyId,
    ).catch((e) => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat());
      }
      throw e;
    });
    props.onCreated(checklist);
  };

  return (
    <div className={"flex flex-col space-y-4"}>
      <div>
        <Input type={"text"} label={"Materiaaltype"} value={materialType} onChange={setMaterialType} />
      </div>
      <div>
        <Select
          label={"Hijscategorie"}
          options={liftingCategoryOptions}
          value={liftingCategory}
          onChange={setLiftingCategory}
        />
      </div>
      <div>
        <Select
          label={"Bedrijf"}
          options={Object.fromEntries(companyOptions)}
          value={companyId}
          onChange={setCompanyId}
        />
      </div>
      {errors.length > 0 && (
        <div className={"text-red-600 text-sm"}>
          {errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
      <ModalFooter text={"Opslaan"} icon={faSave} onSubmit={save} disabled={!materialType || companyId === ""} />
    </div>
  );
};
